import React from "react";
import { motion } from "framer-motion";
import { Shield, Lock, Eye, Database, Cookie, Mail } from "lucide-react";
import { Container } from "../components/layout/container";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";

const sectionIcons = [Database, Eye, Lock, Cookie, Shield, Mail];

export default function PrivacyPolicyPage() {
  const { t } = useLanguage();
  const privacyData = t.privacy;

  const sections = (privacyData?.sections || []).map((sec, idx) => ({
    icon: sectionIcons[idx] || Shield,
    title: sec.title,
    content: sec.content,
  }));
  return (
    <div className="min-h-screen bg-[#0B0B0F]">
      <SEO 
        title={privacyData?.seo?.title || "Privacy Policy | Nandan Priyadarshi"}
        description={privacyData?.seo?.description || "Privacy policy for Nandan Priyadarshi's portfolio website. Learn how we collect, use, and protect your personal information."}
        canonicalUrl="/privacy-policy"
        keywords={privacyData?.seo?.keywords || "Privacy Policy, Data Protection, Nandan Priyadarshi"}
      />
      {/* Hero Section */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-slate-500/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[100px]" />
        </div>

        <Container className="relative">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Shield className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                {privacyData?.eyebrow || 'Legal'}
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4">
              {privacyData?.title || 'Privacy Policy'}
            </h1>
            
            <p className="text-lg text-white/60 mb-2">
              {privacyData?.description || "Your privacy matters to us. Here's how we handle your data."}
            </p>
            
            <p className="text-sm text-white/40">
              {privacyData?.lastUpdated || 'Last updated: January 2026'}
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <Container>
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <motion.div
              className="mb-12 p-6 rounded-2xl bg-white/5 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p className="text-white/70 leading-relaxed">
                {privacyData?.intro || `This Privacy Policy describes how Nandan Priyadarshi ("we," "us," or "our") collects, uses, and shares information about you when you visit our portfolio website. By using our website, you agree to the collection and use of information in accordance with this policy.`}
              </p>
            </motion.div>

            {/* Sections */}
            <div className="space-y-8">
              {sections.map((section, index) => (
                <motion.div
                  key={section.title}
                  className="group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 + index * 0.05 }}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                        <section.icon className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white mb-2">
                        {section.title}
                      </h2>
                      <p className="text-sm text-white/60 leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Footer Note */}
            <motion.div
              className="mt-12 pt-8 border-t border-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <p className="text-sm text-white/40 text-center">
                {privacyData?.footerNote || `This privacy policy is effective as of January 1, 2026 and will remain in effect except with respect to any changes in its provisions in the future, which will be in effect immediately after being posted on this page.`}
              </p>
            </motion.div>
          </div>
        </Container>
      </section>
    </div>
  );
}
